import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import type { AllocationResponse } from '../../services/investment-plan-service';
import { AllocationBars, DCAStatusLine } from '../plan/AllocationBars';
import { EmptyLine, NavigateFn, SectionCard } from './shared';

/**
 * Dashboard "Plan" card (issue #31): allocation against target, the DCA
 * glide path, the top open recommendations and the week's high-conviction
 * signals. Read from GET /portfolio/allocation, which never calls Jev.
 */
export const PlanCard: React.FC<{ data: AllocationResponse | null; onNavigate: NavigateFn }> = ({ data, onNavigate }) => {
  if (!data) return null;
  if (!data.configured || !data.allocation) {
    return (
      <SectionCard title="Plan" action={{ label: 'Set up', link: { to: '/investment-plan' } }} onNavigate={onNavigate}>
        <EmptyLine>No investment plan yet. Write down a target mix and FolioWorth will check it every night.</EmptyLine>
      </SectionCard>
    );
  }
  const recs = (data.open_recommendations ?? []).slice(0, 3);
  const risk = data.last_check?.risk;
  const hc = (data.signals ?? []).filter((s) => s.high_conviction).slice(0, 3);
  return (
    <SectionCard
      title="Plan"
      subtitle={`v${data.plan_version}${data.enabled ? '' : ' · nightly check off'}`}
      action={{ label: 'Recommendations', link: { to: '/recommendations' } }}
      onNavigate={onNavigate}
    >
      <AllocationBars classes={data.allocation.classes.filter((c) => c.status !== 'no_target' || parseFloat(c.pct) >= 1)} compact />
      <Box sx={{ mt: 1.5 }}><DCAStatusLine dca={data.dca} line={data.dca_line} /></Box>
      {risk && (
        <Typography variant="body2" sx={{ mt: 1 }} data-testid="plan-risk">
          Risk: <strong>{risk.label}</strong> (target {risk.target_label}{risk.vs_target !== 'matches' ? `, ${risk.vs_target}` : ''})
          {' · '}
          {risk.tiers.map((t) => `${t.tier.replace('_', ' ')} ${t.pct_of_equity}%`).join(' · ')}
        </Typography>
      )}
      <Box sx={{ mt: 1.5 }} aria-label="Open recommendations">
        {recs.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>No open recommendations.</Typography>
        ) : recs.map((r) => (
          <Typography key={r.id} component="div" variant="body2" sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.25 }}>
            <Chip size="small" label={r.action} sx={{ height: 20 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</span>
          </Typography>
        ))}
      </Box>
      {hc.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
          High-conviction signals: {hc.map((s) => `${s.symbol} ${s.choice.replace('_', ' ')}`).join(' · ')}
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'var(--text-secondary)' }}>{data.disclaimer}</Typography>
    </SectionCard>
  );
};
